import BASE_URL from "components/BaseUrl/apiConfig";
import React, { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

export default function ExpanseTypeWiseLineChart() {
  const [expensestData, setExpenseData] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [currency, setCurrency] = useState("");
  const [errorMessage, setErrorMessage] = useState(""); // For error message
  const skipRecords = (data, count) => data.slice(count);

  const storedUserData = localStorage.getItem("userData");
  let groupId = "";

  if (storedUserData) {
    const userData = JSON.parse(storedUserData);
    groupId = userData.groupId;
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          `${BASE_URL}/api/Analytics/GetExpensesByTypewise?groupId=${groupId}&year=${selectedYear}`
        );
        let data = await response.json();

        // Skip the first N records
        const skippedData = skipRecords(data, 2); // Change '2' to the desired number of records to skip

        if (skippedData.length === 0) {
          setErrorMessage("No data available for the selected year.");
        } else {
          setErrorMessage(""); // Reset error message if data is found
        }

        setExpenseData(skippedData);
      } catch (error) {
        console.error("Error fetching data:", error);
        setErrorMessage("Failed to fetch data.");
      }
    };

    fetchData();
  }, [selectedYear]);

  useEffect(() => {
    const fetchCurrencyData = async () => {
      try {
        const response = await fetch(
          `${BASE_URL}/api/Users/GetCurrencyByUserId?groupId=${groupId}`
        );
        const data = await response.json();
        setCurrency(data.currency);
      } catch (error) {
        console.error("Error fetching currency data:", error);
      }
    };

    fetchCurrencyData();
  }, []);

  const handleYearChange = (e) => {
    setSelectedYear(parseInt(e.target.value, 10));
  };

  const getMonthName = (monthNumber) => {
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return monthNames[monthNumber - 1];
  };

  const transposeData = (data) => {
    const transposedData = {};
    data.forEach((expense) => {
      if (!transposedData[expense.expenseDescType]) {
        transposedData[expense.expenseDescType] = {};
      }
      transposedData[expense.expenseDescType][expense.month] =
        expense.totalExpenses;
    });
    return transposedData;
  };

  const transposedExpenseData = transposeData(expensestData);

  // Prepare chart data
  const labels = Array.from({ length: 12 }, (_, i) => getMonthName(i + 1));
  const datasets = Object.keys(transposedExpenseData)
    .map((expenseType, index) => {
      const data = Array.from(
        { length: 12 },
        (_, i) => transposedExpenseData[expenseType][i + 1] || 0
      );

      // Check if the dataset contains any non-zero values
      const hasData = data.some((value) => value !== 0);

      if (!hasData) {
        return null; // Skip this dataset if all values are zero
      }

      const colors = [
        "rgba(255, 99, 132, 0.4)", // Soft Red
        "rgba(54, 162, 235, 0.4)", // Soft Blue
        "rgba(255, 206, 86, 0.4)", // Soft Yellow
        "rgba(99, 255, 132, 0.4)", // Soft Light Green
        "rgba(159, 64, 255, 0.4)", // Soft Violet
        "rgba(162, 54, 235, 0.4)", // Soft Indigo
        "rgba(192, 192, 75, 0.4)", // Soft Olive Green
        "rgba(153, 102, 255, 0.4)", // Soft Purple
        "rgba(255, 159, 64, 0.4)", // Soft Orange
        "rgba(75, 192, 192, 0.4)", // Soft Teal
        "rgba(206, 255, 86, 0.4)", // Soft Lime
        "rgba(102, 153, 255, 0.4)", // Soft Light Blue
      ];

      return {
        label: expenseType,
        data,

        borderWidth: 2, // Set line thickness (make it thinner, e.g., 2px)
        pointRadius: 3,
        fill: false,
      };
    })
    .filter((dataset) => dataset !== null); // Remove null datasets

  const chartData = {
    labels,
    datasets,
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: `Category Wise Expenses (${currency})`,
      },
      tooltip: {
        callbacks: {
          // Customizing the tooltip title
          title: function (tooltipItem) {
            // Return the label (expenseType) for the current dataset
            return tooltipItem[0].dataset.label;
          },
          // Customizing the tooltip body
          label: function (tooltipItem) {
            const value = tooltipItem.raw;
            const month = tooltipItem.label;
            return `${month}: ${currency} ${value}`; // Showing month and value
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Expenses",
        },
      },
    },
  };

  return (
    <>
      <div className="relative flex flex-col min-w-0 break-words bg-white w-full shadow-lg rounded">
        <div className="rounded mr-4 pl-4 mt-4 bg-transparent">
          <div className="flex justify-between">
            <h2 className="text-blueGray-700 text-xl font-semibold">
              Category Wise Expenses Line Chart ({currency})
            </h2>
            <select
              className="bg-white text-black border border-gray-300 rounded px-4 py-2"
              value={selectedYear}
              onChange={handleYearChange}
            >
              <option value={new Date().getFullYear()}>
                {new Date().getFullYear()}
              </option>
              <option value={new Date().getFullYear() - 1}>
                {new Date().getFullYear() - 1}
              </option>
            </select>
          </div>
        </div>
        <div className="p-4 flex-auto">
          {expensestData.length > 0 ? (
            <Line data={chartData} options={chartOptions} height={100} />
          ) : (
            <p className="text-center text-red-500">
              {errorMessage || "No data available for the selected year."}
            </p>
          )}
        </div>
      </div>
    </>
  );
}
